import React, { useState, useRef, useEffect } from "react";
import {
  FiChevronDown,
  FiUser,
  FiSettings,
  FiLogOut,
  FiLoader,
} from "react-icons/fi";
import { useAuthStore } from "../../store/useAuthStore";
import { useUserLogout } from "../../custom-hooks/queries";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { getCleanedUrl } from "../../utils/HelperMethod";

export default function UserMenu({ onProfile, onSettings }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();
  const navigate = useNavigate();

  // Get user data and logout action from auth store
  const { token, role, user, logout: clearAuth } = useAuthStore();

  const hasAvatar =
    user?.avatar &&
    typeof user.avatar === "string" &&
    user.avatar.trim() !== "";

  // Logout API mutation
  const { mutate: logoutApi, isPending: isLoggingOut } = useUserLogout();

  // close when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Don't render if not logged in
  if (!token) {
    return null;
  }

  // Handle logout
  const handleLogout = () => {
    setOpen(false);

    logoutApi(undefined, {
      onSuccess: () => {
        clearAuth();
        toast.success("Logged out successfully");
        navigate("/signin");
      },
      onError: () => {
        // Even if API fails, clear local auth and redirect
        clearAuth();
        toast.success("Logged out successfully");
        navigate("/signin");
      },
    });
  };

  // Get display values from auth store
  const displayName =
    user?.name ||
    user?.businessName ||
    `${user?.firstName || ""} ${user?.lastName || ""}`.trim() ||
    "User";

  const displayRole = role
    ? role.charAt(0).toUpperCase() + role.slice(1).toLowerCase()
    : "User";

  const firstLetter = displayName?.charAt(0)?.toUpperCase() || "U";
  const displayAvatar = getCleanedUrl(user?.avatar) || firstLetter;

  return (
    <div className="relative" ref={ref}>
      {/* User Button */}
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center bg-gray-100 rounded-xl px-4 py-2 border border-gray-200 hover:bg-gray-200 transition"
      >
        {hasAvatar ? (
          <img
            src={displayAvatar}
            alt={displayName}
            className="h-12 w-12 rounded-full object-cover border border-gray-300"
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = "";
            }}
          />
        ) : (
          <div className="h-12 w-12 rounded-full bg-successGreen flex items-center justify-center text-white font-semibold text-lg border border-gray-300">
            {firstLetter}
          </div>
        )}

        <div className="ml-3 text-left">
          <p className="font-semibold text-gray-900">{displayName}</p>
          <p className="text-sm text-gray-600 -mt-1">{displayRole}</p>
        </div>

        <FiChevronDown className="ml-3 text-gray-700" size={20} />
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute right-0 mt-2 w-[200px] bg-white rounded-xl shadow-xl border border-gray-200 z-50 overflow-hidden">
          {/* Profile */}
          <DropdownItem
            icon={<FiUser size={18} />}
            label="Profile"
            onClick={() => {
              setOpen(false);
              onProfile?.();
            }}
            active
          />

          {/* Settings */}
          {/* <DropdownItem
            icon={<FiSettings size={18} />}
            label="Settings"
            onClick={() => {
              setOpen(false);
              onSettings?.();
            }}
          /> */}

          {/* Logout */}
          <DropdownItem
            icon={
              isLoggingOut ? (
                <FiLoader size={18} className="animate-spin" />
              ) : (
                <FiLogOut size={18} />
              )
            }
            label={isLoggingOut ? "Logging out..." : "Logout"}
            onClick={handleLogout}
            disabled={isLoggingOut}
          />
        </div>
      )}
    </div>
  );
}

/* Single item in dropdown */
function DropdownItem({
  icon,
  label,
  onClick,
  active = false,
  disabled = false,
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`
        w-full flex items-center gap-3 px-4 py-3 text-left 
        ${active ? "bg-[#01B9C6]/20" : "hover:bg-gray-100"}
        ${disabled ? "opacity-50 cursor-not-allowed" : ""}
        transition text-gray-800
      `}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}
